import { Fragment, Ref, forwardRef, useRef } from 'react';
import { useMotionValueEvent, useScroll } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AspectRatio, IconButton, Text } from '@cellula/react';
import { styled } from 'styled-components';
import { clamp } from '@cortex-shim/clamp';
import { tokens } from '@cellula/react-theme-patient';
import { useResolve, useService } from '@visiba/instantiation';
import { UniversalConfig } from '@application/universal_config.service';
import { RouterService } from '@cortex-shim/router.service';

const StyledNavigationBar = styled.div`
	display: flex;
	padding: 16px 22px;
	position: fixed;
	align-items: center;
	justify-content: center;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1;

	&:after {
		content: '';
		position: absolute;
		inset: 0;
		z-index: -1;

		--extended-by: 100px;
		bottom: calc(-1 * var(--extended-by));
		top: -1px;

		--cutoff: calc(100% - var(--extended-by));

		-webkit-mask-image: linear-gradient(to bottom, black 0, black var(--cutoff), transparent var(--cutoff));

		--blur: 15px;
		backdrop-filter: blur(var(--blur));
		-webkit-backdrop-filter: blur(var(--blur));

		background: linear-gradient(180deg, var(--bg-page-color) 10%, transparent 100%);

		user-select: none;
		pointer-events: none;

		${tokens.breakpointQuery.large} {
			background: linear-gradient(180deg, #fff 10%, transparent 100%);
		}
	}

	&:before {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 1px;
		opacity: var(--_opacity, 0);
		background: rgba(71, 51, 51, 0.06);

		${tokens.breakpointQuery.large} {
			opacity: 1;
		}
	}
`;

const StyledInner = styled.div`
	align-items: center;
	justify-content: space-between;
	display: flex;
	width: 100%;
	max-width: 1532px;
`;

function NavigationBar(
	props: React.PropsWithChildren<{
		title?: string;
	}>,
	__: Ref<HTMLDivElement>,
) {
	const universalConfig = useResolve(UniversalConfig);

	const ref = useRef<HTMLDivElement>(null);
	const { scrollY } = useScroll();
	useMotionValueEvent(scrollY, 'change', (latest) => {
		ref.current?.style.setProperty('--_opacity', clamp(0, latest / 100, 1).toString());
	});

	const routerService = useService(RouterService);

	return (
		<Fragment>
			<StyledNavigationBar ref={ref}>
				<StyledInner>
					<Link to='/'>
						<div
							style={{
								width: 48,
							}}
						>
							<AspectRatio.Root ratio={1.6}>
								<AspectRatio.Img
									src={universalConfig.logo}
									alt=''
									style={{
										objectFit: 'contain',
										width: '100%',
										height: '100%',
										borderRadius: 'var(--radius-2)',
									}}
								/>
							</AspectRatio.Root>
						</div>
					</Link>

					{props.title ? <Text variant='bodySemiBold'>{props.title}</Text> : <div />}

					<IconButton
						icon='user'
						variant='neutral'
						size='medium'
						text=''
						onClick={() => {
							routerService.navigateByUrl('/settings');
						}}
					/>
				</StyledInner>
			</StyledNavigationBar>
		</Fragment>
	);
}

const _NavigationBar = forwardRef(NavigationBar) as typeof NavigationBar;
export { _NavigationBar as NavigationBar };
